import { AgentAvailability } from 'models/agent_status';
import { DomainError } from 'scripts/application/lib/error';
import Err from 'models/err';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_interactive_handler';
import moment from 'moment';
import ShowToast from 'actions/toast_deprecated/show_toast';
import { ToastType } from 'models/toast_deprecated';

export default class UpdateAgentStatus {
  constructor(context) {
    this.context = context;
  }

  get store() {
    return this.context.stores.liveboard;
  }

  getAgentName(agentId) {
    const agent = this.context.stores.agents.findBy({ id: agentId });
    return agent?.getDisplayName() || 'Agent';
  }

  handleError(err, agentId) {
    this.store.resetPending();
    if (
      err instanceof DomainError &&
      err.errors[0].code === Err.Code.INVALID_STATE &&
      err.errors[0].attr === 'status.availability'
    ) {
      const agentName = this.getAgentName(agentId);
      this.context.executeAction(ShowToast, {
        type: ToastType.ERROR,
        message: `Unable to set ${agentName} away because they are logged out.`,
      });
      // Update agent status in pending store since we know is OFFLINE
      this.setLiveboardAgentStatusPending(agentId, AgentAvailability.OFFLINE, null);
    } else {
      handleCommonErrors(this.context, err);
    }
  }

  handleSuccess(agentId, newStatus, reasonId) {
    this.trackAnalytics(agentId, newStatus, reasonId);
    this.store.commitPending();
    const agentName = this.getAgentName(agentId);
    this.context.executeAction(ShowToast, {
      type: ToastType.SUCCESS,
      message:
        newStatus === AgentAvailability.AWAY ? `${agentName} has been set away` : `${agentName} has been logged out`,
    });
  }

  run({ agentId, newStatus, reasonId = null }) {
    const updatePayload = { availability: newStatus };
    if (newStatus === AgentAvailability.AWAY) {
      updatePayload.awayReasonId = reasonId;
    }

    this.setLiveboardAgentStatusPending(agentId, newStatus, newStatus === AgentAvailability.AWAY ? reasonId : null);

    this.context.gateways.agentStatus
      .update({ agentId }, updatePayload)
      .then(() => this.handleSuccess(agentId, newStatus, reasonId))
      .catch(err => this.handleError(err, agentId));
  }

  setLiveboardAgentStatusPending(agentId, newStatus, reasonId) {
    const liveboard = this.store.get();
    const agentStatus = liveboard?.utilization?.find(agent => agent.agentId === agentId);
    if (!agentStatus) {
      return;
    }
    if (agentStatus.status !== newStatus) {
      agentStatus.setStatus(newStatus);
      agentStatus.setStatusUpdatedAt(moment().toISOString());
    }
    agentStatus.setStatusReasonId(reasonId);
    agentStatus.incrementStatusVersion();
    this.store.setPending(liveboard);
  }

  trackAnalytics(agentId, newStatus, reasonId) {
    const eventData = {
      targetAgentId: agentId,
      newStatus,
    };

    if (newStatus === AgentAvailability.AWAY && reasonId) {
      const reason = this.context.stores.agentStatusReasons.find(reasonId);
      if (reason) {
        eventData.reasonId = reason.id;
        eventData.reasonName = reason.name;
      }
    }

    this.context.analytics.track('Agent Status Updated By Manager', eventData);
  }
}
